/**
 * TerminalViewer -- shared live log streaming component with heartbeat cursor.
 *
 * Features:
 * - ANSI escape code parsing (colors, bold, dim, underline, etc.)
 * - Auto-scroll to bottom with scroll-lock when user scrolls up
 * - Heartbeat cursor: animated blinking cursor color = liveness indicator
 *   (green <5s, yellow 5-15s, red >15s, grey when inactive)
 * - Search overlay (Cmd/Ctrl+F): highlights matches, count, next/prev
 * - Optional line numbers and timestamps
 * - Download, copy, and clear buttons
 */

import {
  Component,
  createSignal,
  createEffect,
  createMemo,
  For,
  Show,
  onMount,
  onCleanup,
} from "solid-js";
import type {
  TerminalViewerProps,
  TerminalLine,
  LivenessStatus,
} from "./types";
import { HEARTBEAT_THRESHOLDS } from "./types";
import { parseAnsi, stripAnsi } from "./ansi-parser";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine the liveness status from a heartbeat timestamp.
 */
export function getLivenessStatus(
  lastHeartbeat: number | undefined,
  isActive: boolean | undefined,
): LivenessStatus {
  if (!isActive) return "inactive";
  if (lastHeartbeat == null || lastHeartbeat === 0) return "inactive";

  const elapsed = Date.now() - lastHeartbeat;
  if (elapsed < HEARTBEAT_THRESHOLDS.stale) return "live";
  if (elapsed < HEARTBEAT_THRESHOLDS.dead) return "stale";
  return "dead";
}

/**
 * Map liveness status to a CSS modifier class.
 */
function cursorClass(status: LivenessStatus): string {
  switch (status) {
    case "live":
      return "tv-cursor--live";
    case "stale":
      return "tv-cursor--stale";
    case "dead":
      return "tv-cursor--dead";
    case "inactive":
    default:
      return "tv-cursor--inactive";
  }
}

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Format an epoch timestamp to a locale time string.
 */
function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleTimeString();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Renders a single ANSI-parsed text string.
 */
const AnsiText: Component<{ text: string }> = (props) => {
  const segments = createMemo(() => parseAnsi(props.text));

  return (
    <>
      <For each={segments()}>
        {(segment) => (
          <span class={segment.classes.join(" ")}>{segment.text}</span>
        )}
      </For>
    </>
  );
};

// ---------------------------------------------------------------------------
// SVG icons (inline to avoid external dependencies)
// ---------------------------------------------------------------------------

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const ClearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ScrollDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const TerminalViewer: Component<TerminalViewerProps> = (props) => {
  let bodyRef: HTMLDivElement | undefined;
  let searchInputRef: HTMLInputElement | undefined;

  // ---- State ----
  const [searchQuery, setSearchQuery] = createSignal("");
  const [isSearchOpen, setIsSearchOpen] = createSignal(false);
  const [currentSearchIndex, setCurrentSearchIndex] = createSignal(0);
  const [autoScroll, setAutoScroll] = createSignal(true);
  const [liveness, setLiveness] = createSignal<LivenessStatus>("inactive");

  // ---- Derived data ----

  /** Lines capped at maxLines (drop oldest). */
  const visibleLines = createMemo((): TerminalLine[] => {
    const max = props.maxLines ?? 0;
    if (max > 0 && props.lines.length > max) {
      return props.lines.slice(props.lines.length - max);
    }
    return props.lines;
  });

  /** Lines matching the search query. */
  const filteredLines = createMemo((): TerminalLine[] => {
    const q = searchQuery().toLowerCase().trim();
    if (!q) return visibleLines();
    return visibleLines().filter((line) =>
      stripAnsi(line.text).toLowerCase().includes(q),
    );
  });

  /** Number of search matches across all visible lines. */
  const searchMatchCount = createMemo((): number => {
    const q = searchQuery().toLowerCase().trim();
    if (!q) return 0;
    return visibleLines().filter((line) =>
      stripAnsi(line.text).toLowerCase().includes(q),
    ).length;
  });

  // ---- Heartbeat polling ----

  onMount(() => {
    const interval = setInterval(() => {
      setLiveness(getLivenessStatus(props.lastHeartbeat, props.isActive));
    }, 1_000);
    onCleanup(() => clearInterval(interval));
  });

  // ---- Auto-scroll on new lines ----

  createEffect(() => {
    // Track lines length to trigger the effect
    const _len = props.lines.length;
    if (_len > 0 && autoScroll() && bodyRef) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        if (bodyRef) {
          bodyRef.scrollTop = bodyRef.scrollHeight;
        }
      });
    }
  });

  // ---- Scroll detection ----

  const handleScroll = () => {
    if (!bodyRef) return;
    const isAtBottom =
      bodyRef.scrollHeight - bodyRef.scrollTop <= bodyRef.clientHeight + 50;
    setAutoScroll(isAtBottom);
  };

  const scrollToBottom = () => {
    if (bodyRef) {
      bodyRef.scrollTop = bodyRef.scrollHeight;
      setAutoScroll(true);
    }
  };

  // ---- Search ----

  const toggleSearch = () => {
    const willOpen = !isSearchOpen();
    setIsSearchOpen(willOpen);
    if (!willOpen) {
      setSearchQuery("");
      setCurrentSearchIndex(0);
    } else {
      setTimeout(() => searchInputRef?.focus(), 0);
    }
  };

  const navigateSearch = (direction: "prev" | "next") => {
    const count = searchMatchCount();
    if (count === 0) return;

    setCurrentSearchIndex((prev) => {
      if (direction === "next") return (prev + 1) % count;
      return prev === 0 ? count - 1 : prev - 1;
    });

    // Scroll the matched element into view
    const elements = bodyRef?.querySelectorAll("[data-search-match]");
    const target = elements?.[currentSearchIndex()];
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ---- Keyboard shortcuts ----

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleSearch();
    } else if (e.key === "Escape" && isSearchOpen()) {
      setIsSearchOpen(false);
      setSearchQuery("");
      setCurrentSearchIndex(0);
    } else if (e.key === "Enter" && isSearchOpen()) {
      e.preventDefault();
      navigateSearch(e.shiftKey ? "prev" : "next");
    }
  };

  createEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));
  });

  // ---- Download ----

  const handleDownload = () => {
    const content = visibleLines()
      .map((line) => {
        const ts = line.timestamp
          ? `[${new Date(line.timestamp).toISOString()}] `
          : "";
        const level = line.level ? `[${line.level.toUpperCase()}] ` : "";
        return `${ts}${level}${stripAnsi(line.text)}`;
      })
      .join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `terminal-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    props.onDownload?.();
  };

  // ---- Copy ----

  const handleCopy = async () => {
    const content = visibleLines()
      .map((line) => stripAnsi(line.text))
      .join("\n");
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // Fallback: noop in environments without clipboard API
    }
  };

  // ---- Search highlighting ----

  /**
   * Returns true if a line matches the current search query (for marking
   * search-matched lines so the navigator can scroll to them).
   */
  const isSearchMatch = (line: TerminalLine): boolean => {
    const q = searchQuery().toLowerCase().trim();
    if (!q) return false;
    return stripAnsi(line.text).toLowerCase().includes(q);
  };

  /**
   * Highlight search matches by wrapping them in <mark> tags.
   * Returns an HTML string to be used with innerHTML.
   * HTML-escapes the text first to prevent XSS from log content.
   */
  const highlightText = (text: string): string => {
    const q = searchQuery().trim();
    if (!q) return "";
    const plain = stripAnsi(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    const regex = new RegExp(
      `(${escapeRegex(q)})`,
      "gi",
    );
    return plain.replace(
      regex,
      '<mark class="tv-search-highlight">$1</mark>',
    );
  };

  // ---- Level class ----

  const levelClass = (level?: string): string => {
    switch (level) {
      case "warn":
        return "tv-level-warn";
      case "error":
        return "tv-level-error";
      case "debug":
        return "tv-level-debug";
      case "info":
      default:
        return "tv-level-info";
    }
  };

  // ---- Render ----

  return (
    <div class="tv-container" data-testid="terminal-viewer">
      {/* ---- Header ---- */}
      <div class="tv-header">
        <div class="tv-header-left">
          <div class="tv-traffic-lights">
            <div class="tv-traffic-light tv-traffic-red" />
            <div class="tv-traffic-light tv-traffic-yellow" />
            <div class="tv-traffic-light tv-traffic-green" />
          </div>
          <span class="tv-title">{props.title || "Terminal"}</span>
        </div>

        <div class="tv-header-right">
          {/* Liveness indicator */}
          <Show when={props.isActive}>
            <div
              class="tv-header-right"
              style={{ gap: "0.375rem", "font-size": "0.75rem" }}
              data-testid="liveness-indicator"
            >
              <span
                class={`tv-cursor ${cursorClass(liveness())}`}
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  "border-radius": "9999px",
                  "margin-left": "0",
                }}
              />
              <span style={{ color: "var(--tv-text-muted)" }}>
                {liveness() === "live"
                  ? "Live"
                  : liveness() === "stale"
                  ? "Stale"
                  : liveness() === "dead"
                  ? "No signal"
                  : "Inactive"}
              </span>
            </div>
          </Show>

          {/* Line count */}
          <span class="tv-line-count" data-testid="line-count">
            {filteredLines().length}
            {searchQuery() ? `/${visibleLines().length}` : ""} lines
          </span>

          {/* Search toggle */}
          <button
            class={`tv-btn ${isSearchOpen() ? "tv-btn--active" : ""}`}
            onClick={toggleSearch}
            title="Search (Cmd/Ctrl+F)"
            data-testid="search-button"
          >
            <SearchIcon />
          </button>

          {/* Download */}
          <button
            class="tv-btn"
            onClick={handleDownload}
            title="Download logs"
            data-testid="download-button"
          >
            <DownloadIcon />
          </button>

          {/* Copy */}
          <button
            class="tv-btn"
            onClick={handleCopy}
            title="Copy to clipboard"
            data-testid="copy-button"
          >
            <CopyIcon />
          </button>

          {/* Clear (calls onSearch with empty to signal clear) */}
          <button
            class="tv-btn"
            onClick={() => props.onSearch?.("")}
            title="Clear terminal"
            data-testid="clear-button"
          >
            <ClearIcon />
          </button>

          {/* Scroll to bottom */}
          <button
            class={`tv-btn ${!autoScroll() ? "tv-btn--active" : ""}`}
            onClick={scrollToBottom}
            title="Scroll to bottom"
            data-testid="scroll-button"
          >
            <ScrollDownIcon />
          </button>
        </div>
      </div>

      {/* ---- Search bar ---- */}
      <Show when={isSearchOpen()}>
        <div class="tv-search" data-testid="search-bar">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery()}
            onInput={(e) => {
              setSearchQuery(e.currentTarget.value);
              setCurrentSearchIndex(0);
              props.onSearch?.(e.currentTarget.value);
            }}
            placeholder="Search logs..."
            class="tv-search-input"
            data-testid="search-input"
          />
          <Show when={searchMatchCount() > 0}>
            <span class="tv-search-count" data-testid="search-count">
              {currentSearchIndex() + 1} / {searchMatchCount()}
            </span>
            <button
              class="tv-btn"
              onClick={() => navigateSearch("prev")}
              title="Previous match"
            >
              <ChevronUpIcon />
            </button>
            <button
              class="tv-btn"
              onClick={() => navigateSearch("next")}
              title="Next match"
            >
              <ChevronDownIcon />
            </button>
          </Show>
          <button class="tv-btn" onClick={toggleSearch} title="Close search">
            <CloseIcon />
          </button>
        </div>
      </Show>

      {/* ---- Terminal body ---- */}
      <div
        ref={bodyRef}
        class="tv-body"
        style={{
          ...(props.height
            ? { height: `${props.height}px` }
            : { "max-height": "24rem" }),
        }}
        onScroll={handleScroll}
        data-testid="terminal-body"
      >
        <Show
          when={visibleLines().length > 0}
          fallback={
            <div class="tv-empty" data-testid="empty-state" role="status">
              <div class="tv-empty-text">
                {props.isActive ? "Waiting for output..." : "No output"}
              </div>
            </div>
          }
        >
          <For each={filteredLines()}>
            {(line, index) => (
              <div
                class={`tv-line ${levelClass(line.level)}`}
                data-testid={`terminal-line-${index()}`}
                {...(isSearchMatch(line)
                  ? { "data-search-match": "true" }
                  : {})}
              >
                {/* Line number */}
                <Show when={props.showLineNumbers !== false}>
                  <span class="tv-line-number" data-testid="line-number">
                    {index() + 1}
                  </span>
                </Show>

                {/* Timestamp */}
                <Show when={props.showTimestamps && line.timestamp}>
                  <span class="tv-line-timestamp" data-testid="line-timestamp">
                    {formatTimestamp(line.timestamp!)}
                  </span>
                </Show>

                {/* Content */}
                <Show
                  when={searchQuery().trim()}
                  fallback={
                    <span class="tv-line-content">
                      <AnsiText text={line.text} />
                    </span>
                  }
                >
                  <span
                    class="tv-line-content"
                    innerHTML={highlightText(line.text)}
                  />
                </Show>
              </div>
            )}
          </For>

          {/* Heartbeat cursor */}
          <div class="tv-cursor-row" data-testid="cursor-row">
            <Show when={props.showLineNumbers !== false}>
              <span class="tv-line-number">
                {filteredLines().length + 1}
              </span>
            </Show>
            <span class="tv-cursor-prompt">$ </span>
            <span
              class={`tv-cursor ${cursorClass(liveness())}`}
              data-testid="heartbeat-cursor"
              data-liveness={liveness()}
            />
          </div>
        </Show>
      </div>

      {/* ---- Floating new-logs button ---- */}
      <Show when={!autoScroll() && props.lines.length > 0}>
        <button
          class="tv-scroll-indicator"
          onClick={scrollToBottom}
          data-testid="new-logs-button"
        >
          New logs available
          <ScrollDownIcon />
        </button>
      </Show>
    </div>
  );
};

export default TerminalViewer;
